import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { infoNextLevel } from "../utils/levelSystem";
import GameEngine from "../components/GameEngine";
import { Play, CheckCircle, X, Loader2 } from "lucide-react";
import { styles } from "../utils/globalStyles";

export default function GameCenter({ user }) {
  // User Data
  const [siswaStats, setSiswaStats] = useState(null);
  const [quests, setQuests] = useState([]);

  // Game State
  const [activeQuest, setActiveQuest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [user]);

  // --- STYLE ANIMASI POPUP (Disuntikkan via JS) ---
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = `
      @keyframes popIn {
        0% { transform: scale(0.9); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  async function fetchData() {
    if (!user) return;
    setLoading(true);

    const { data: dataSiswa } = await supabase
      .from("siswa")
      .select("id, nama_lengkap, total_exp, level")
      .ilike("nama_lengkap", user.nama)
      .single();

    if (dataSiswa) {
      setSiswaStats(dataSiswa);

      const { data: dataQuest } = await supabase
        .from("siswa_quests")
        .select(
          `
          id, status, skor_terakhir,
          library_quests ( id, judul, deskripsi, reward_exp, min_score, konten )
        `
        )
        .eq("siswa_id", dataSiswa.id);

      setQuests(dataQuest || []);
    }
    setLoading(false);
  }

  const handleGameFinish = async (nilaiAkhir, isLulus) => {
    if (!activeQuest || !siswaStats) return;

    try {
      if (isLulus) {
        await supabase
          .from("siswa_quests")
          .update({ status: "SELESAI", skor_terakhir: nilaiAkhir })
          .eq("id", activeQuest.id);

        const newTotalExp =
          siswaStats.total_exp + activeQuest.library_quests.reward_exp;
        const levelInfo = infoNextLevel(newTotalExp);

        await supabase
          .from("siswa")
          .update({ total_exp: newTotalExp, level: levelInfo.currentLevel })
          .eq("id", siswaStats.id);

        alert(
          `Selamat! Misi Selesai. +${activeQuest.library_quests.reward_exp} EXP`
        );
      } else {
        await supabase
          .from("siswa_quests")
          .update({ skor_terakhir: nilaiAkhir })
          .eq("id", activeQuest.id);
      }
    } catch (err) {
      console.error("Gagal save game:", err);
    } finally {
      setActiveQuest(null);
      fetchData();
    }
  };

  // --- STYLE MODAL YANG LEBIH RAPI & CENTER ---
  const modalStyles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0, 0, 0, 0.75)", // Lebih gelap agar fokus
      zIndex: 9999, // Pastikan di atas BottomNav
      display: "flex",
      alignItems: "center", // Center Vertikal
      justifyContent: "center", // Center Horizontal
      padding: "20px", // Jarak aman dari pinggir layar HP
      boxSizing: "border-box",
      backdropFilter: "blur(4px)",
    },
    content: {
      background: "white",
      width: "100%",
      maxWidth: "450px", // Lebar ideal untuk game HP
      maxHeight: "85vh", // Agar tidak kepotong jika layar pendek
      borderRadius: "24px",
      position: "relative",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      animation: "popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
      overflowY: "auto", // Scroll jika konten game panjang
      display: "flex",
      flexDirection: "column",
    },
    headerBar: {
      padding: "15px 20px",
      borderBottom: "1px solid #F1F5F9",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: "#fff",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    gameBody: {
      padding: "20px", // Memberi ruang napas untuk GameEngine
    },
    closeBtn: {
      background: "#F1F5F9",
      border: "none",
      borderRadius: "50%",
      width: "30px",
      height: "30px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#64748B",
      transition: "0.2s",
    },
  };

  if (loading)
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#64748B" }}>
        <Loader2 className="animate-spin" /> Memuat Game...
      </div>
    );

  const levelInfo = siswaStats
    ? infoNextLevel(siswaStats.total_exp)
    : { currentLevel: 1, progress: 0 };

  return (
    <div style={styles.container}>
      {/* HEADER LEVEL */}
      <div
        style={{
          ...styles.headerHero,
          borderRadius: "0 0 20px 20px",
          marginBottom: 20,
        }}
      >
        <h2 style={{ margin: 0 }}>Level {levelInfo.currentLevel}</h2>
        <p style={{ margin: "5px 0 10px 0", opacity: 0.8 }}>
          {siswaStats?.nama_lengkap}
        </p>
        <div
          style={{
            background: "rgba(255,255,255,0.2)",
            height: 10,
            borderRadius: 5,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: `${levelInfo.progress}%`,
              background: "#FACC15",
              height: "100%",
              transition: "1s",
            }}
          ></div>
        </div>
        <div style={{ fontSize: 10, marginTop: 5, textAlign: "right" }}>
          EXP: {siswaStats?.total_exp} / {levelInfo.targetExp}
        </div>
      </div>

      {/* LIST QUEST */}
      <div style={{ padding: "0 20px" }}>
        <h3 style={{ color: "#334155" }}>Misi Tersedia</h3>
        {quests.length === 0 ? (
          <p style={{ color: "#94A3B8", textAlign: "center", marginTop: 30 }}>
            Belum ada misi aktif.
          </p>
        ) : (
          quests.map((item) => (
            <div
              key={item.id}
              style={{
                ...styles.compactCard,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <div>
                <div style={{ fontWeight: "bold", color: "#1E293B" }}>
                  {item.library_quests.judul}
                </div>
                <div style={{ fontSize: 12, color: "#64748B" }}>
                  Reward:{" "}
                  <span style={{ color: "#D97706", fontWeight: "bold" }}>
                    {item.library_quests.reward_exp} EXP
                  </span>
                </div>
              </div>
              {item.status === "SELESAI" ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    color: "#16A34A",
                    fontWeight: "bold",
                    fontSize: 13,
                  }}
                >
                  <CheckCircle size={18} /> Selesai
                </div>
              ) : (
                <button
                  onClick={() => setActiveQuest(item)}
                  style={{
                    background: "#2563EB",
                    color: "white",
                    border: "none",
                    padding: "8px 15px",
                    borderRadius: 20,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontWeight: "bold",
                    fontSize: 12,
                  }}
                >
                  <Play size={12} fill="white" /> MAIN
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* --- MODAL POPUP GAME (CENTERED & RESPONSIVE) --- */}
      {activeQuest && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.content}>
            {/* Header Modal (Judul & Close) */}
            <div style={modalStyles.headerBar}>
              <span style={{ fontWeight: "bold", color: "#334155" }}>
                🎮 Area Permainan
              </span>
              <button
                onClick={() => setActiveQuest(null)}
                style={modalStyles.closeBtn}
              >
                <X size={18} />
              </button>
            </div>

            {/* Area Game */}
            <div style={modalStyles.gameBody}>
              <GameEngine
                questData={activeQuest.library_quests}
                onFinish={handleGameFinish}
                onExit={() => setActiveQuest(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
